import React, { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	CircularProgress,
	Typography,
	Box,
	Chip,
	Grid,
	Paper
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import SyncIcon from "@material-ui/icons/Sync";
import PersonIcon from "@material-ui/icons/Person";
import DescriptionIcon from "@material-ui/icons/Description";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
	dialogContent: {
		minWidth: 500,
		minHeight: 300
	},
	devedorCard: {
		padding: theme.spacing(2),
		marginBottom: theme.spacing(2),
		cursor: "pointer",
		border: "2px solid transparent",
		transition: "all 0.3s ease",
		"&:hover": {
			borderColor: theme.palette.primary.main,
			boxShadow: theme.shadows[4]
		}
	},
	selectedCard: {
		borderColor: theme.palette.primary.main,
		backgroundColor: theme.palette.action.selected
	},
	tituloItem: {
		padding: theme.spacing(1),
		marginBottom: theme.spacing(1),
		cursor: "pointer",
		border: "1px solid #e0e0e0",
		borderRadius: 4,
		transition: "all 0.2s ease",
		"&:hover": {
			backgroundColor: theme.palette.action.hover
		}
	},
	selectedTitulo: {
		backgroundColor: theme.palette.primary.light,
		borderColor: theme.palette.primary.main,
		borderWidth: 2
	},
	infoRow: {
		display: "flex",
		justifyContent: "space-between",
		marginBottom: theme.spacing(0.5)
	}
}));

const SyncContactModal = ({ open, onClose, contactNumber, contactId, codDevedor, onSync }) => {
	const classes = useStyles();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [crmData, setCrmData] = useState(null);
	const [selectedDevedor, setSelectedDevedor] = useState(null);
	const [selectedTitulo, setSelectedTitulo] = useState(null);

	useEffect(() => {
		console.log("[SyncContactModal] useEffect disparado:", { 
			open, 
			contactNumber, 
			codDevedor,
			contactId 
		});
		
		// Só buscar no CRM se o codDevedor não estiver preenchido
		if (open && contactNumber && !codDevedor) {
			console.log("[SyncContactModal] ⚠️ codDevedor NÃO preenchido, iniciando busca CRM");
			fetchCrmData();
		} else if (open && codDevedor) {
			console.log("[SyncContactModal] ✅ codDevedor JÁ PREENCHIDO:", codDevedor);
			// Se já tem codDevedor, mostra mensagem informativa
			setError("Este contato já possui código do devedor preenchido.");
		} else if (open) {
			console.log("[SyncContactModal] ⚠️ Modal aberto mas condições não atendidas");
		}
	}, [open, contactNumber, codDevedor]);

	const fetchCrmData = async () => {
		console.log("[SyncContactModal] 🔍 Iniciando fetchCrmData para número:", contactNumber);
		setLoading(true);
		setError(null);
		setCrmData(null);
		setSelectedDevedor(null);
		setSelectedTitulo(null);

		try {
			console.log("[SyncContactModal] POST /crm/search-debtor com contactNumber:", contactNumber);
			const { data } = await api.post("/crm/search-debtor", {
				contactNumber
			});

			console.log("[SyncContactModal] Resposta CRM:", { 
				success: data.success, 
				totalRegistros: data.total_registros,
				qtdData: data.data?.length 
			});

			if (!data.success || !data.data || data.data.length === 0) {
				console.log("[SyncContactModal] ❌ Nenhum devedor encontrado");
				setError("Nenhum devedor encontrado para este número");
				return;
			}

			console.log("[SyncContactModal] ✅ Dados CRM recebidos");
			setCrmData(data);
		} catch (err) {
			console.error("[SyncContactModal] ❌ Erro ao buscar dados do CRM:", err);
			setError(`Erro ao conectar com o CRM: ${err.response?.data?.message || err.message}`);
		} finally {
			setLoading(false);
		}
	};

	const handleDevedorSelect = (devedor) => {
		setSelectedDevedor(devedor);
		setSelectedTitulo(null); // Reset título quando trocar de devedor
	};

	const handleTituloSelect = (titulo) => {
		setSelectedTitulo(titulo);
	};

	const handleConfirm = () => {
		console.log("[SyncContactModal] handleConfirm chamado", { selectedDevedor, selectedTitulo });
		
		if (!selectedDevedor || !selectedTitulo) {
			console.log("[SyncContactModal] ❌ Erro: falta selecionar devedor ou título");
			setError("Selecione um devedor e um título");
			return;
		}

		const syncData = {
			codDevedor: selectedDevedor.devedor.dev_id.toString(),
			sinistro: selectedTitulo.tit_sinAno > 0 || selectedTitulo.tit_sinNum > 0
				? `${selectedTitulo.tit_sinAno}${selectedTitulo.tit_sinNum}`
				: "",
			name: `${selectedDevedor.devedor.dev_id} - ${selectedDevedor.devedor.nome}`
		};

		console.log("[SyncContactModal] ✅ Dados preparados para sync:", syncData);
		onSync(contactId, syncData);
		handleClose();
	};

	const handleClose = () => {
		console.log("[SyncContactModal] Fechando modal");
		setCrmData(null);
		setSelectedDevedor(null);
		setSelectedTitulo(null);
		setError(null);
		onClose();
	};

	const formatCurrency = (value) => {
		return new Intl.NumberFormat("pt-BR", {
			style: "currency",
			currency: "BRL"
		}).format(value);
	};

	const renderTitulos = () => {
		if (!selectedDevedor || !selectedDevedor.titulos) return null;

		const titulosArray = Object.values(selectedDevedor.titulos);

		return (
			<Box mt={3}>
				<Typography variant="subtitle1" gutterBottom>
					<DescriptionIcon style={{ verticalAlign: "middle", marginRight: 4 }} />
					Selecione o Título:
				</Typography>
				<Box maxHeight={300} overflow="auto">
					{titulosArray.map((titulo, index) => (
						<Paper
							key={index}
							className={`${classes.tituloItem} ${
								selectedTitulo?.tit_id === titulo.tit_id ? classes.selectedTitulo : ""
							}`}
							onClick={() => handleTituloSelect(titulo)}
						>
							<div className={classes.infoRow}>
								<Typography variant="body2">
									<strong>Nº:</strong> {titulo.tit_num}
								</Typography>
								<Chip
									label={titulo.tit_stt}
									size="small"
									color={titulo.tit_stt === 11 ? "primary" : "default"}
								/>
							</div>
							{(titulo.tit_sinAno > 0 || titulo.tit_sinNum > 0) && (
								<div className={classes.infoRow}>
									<Typography variant="body2">
										<strong>Sinistro:</strong> {titulo.tit_sinAno}{titulo.tit_sinNum}
									</Typography>
								</div>
							)}
							<div className={classes.infoRow}>
								<Typography variant="body2">
									<strong>Valor:</strong> {formatCurrency(titulo.tit_valFace)}
								</Typography>
								<Typography variant="body2">
									<strong>Autorizado:</strong> {formatCurrency(titulo.tit_valAutorizado)}
								</Typography>
							</div>
							{titulo.tit_obs && (
								<Typography variant="caption" display="block" color="textSecondary">
									{titulo.tit_obs.substring(0, 100)}
									{titulo.tit_obs.length > 100 ? "..." : ""}
								</Typography>
							)}
						</Paper>
					))}
				</Box>
			</Box>
		);
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
			<DialogTitle>
				<Box display="flex" alignItems="center">
					<SyncIcon style={{ marginRight: 8 }} />
					Sincronizar com CRM
				</Box>
			</DialogTitle>
			<DialogContent className={classes.dialogContent}>
				{loading && (
					<Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
						<CircularProgress />
					</Box>
				)}

				{error && (
					<Alert severity="error" onClose={() => setError(null)}>
						{error}
					</Alert>
				)}

				{!loading && crmData && (
					<>
						<Typography variant="subtitle1" gutterBottom>
							<PersonIcon style={{ verticalAlign: "middle", marginRight: 4 }} />
							Selecione o Devedor ({crmData.total_registros} encontrado{crmData.total_registros > 1 ? "s" : ""}):
						</Typography>

						{crmData.data.map((item, index) => (
							<Paper
								key={index}
								className={`${classes.devedorCard} ${
									selectedDevedor?.devedor.dev_id === item.devedor.dev_id
										? classes.selectedCard
										: ""
								}`}
								onClick={() => handleDevedorSelect(item)}
							>
								<Grid container spacing={2}>
									<Grid item xs={12}>
										<Typography variant="h6">
											{item.devedor.dev_id} - {item.devedor.nome}
										</Typography>
									</Grid>
									<Grid item xs={6}>
										<Typography variant="body2" color="textSecondary">
											<strong>CPF/CNPJ:</strong> {item.devedor.doc}
										</Typography>
										<Typography variant="body2" color="textSecondary">
											<strong>Cliente ID:</strong> {item.devedor.cliente_id}
										</Typography>
									</Grid>
									<Grid item xs={6}>
										<Typography variant="body2" color="textSecondary">
											<strong>Dívida Total:</strong> {formatCurrency(item.devedor.total_divida)}
										</Typography>
										<Typography variant="body2" color="textSecondary">
											<strong>Tipo:</strong> {item.devedor.tipo}
										</Typography>
									</Grid>
									{item.devedor.observacao && (
										<Grid item xs={12}>
											<Typography variant="caption" color="textSecondary">
												<strong>Obs:</strong> {item.devedor.observacao.trim().substring(0, 100)}
											</Typography>
										</Grid>
									)}
									<Grid item xs={12}>
										<Chip
											label={`${Object.keys(item.titulos).length} título(s)`}
											size="small"
											color="primary"
											icon={<DescriptionIcon />}
										/>
										<Chip
											label={`${Object.keys(item.telefones).length} telefone(s)`}
											size="small"
											style={{ marginLeft: 8 }}
										/>
									</Grid>
								</Grid>
							</Paper>
						))}

						{renderTitulos()}
					</>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} color="secondary">
					Cancelar
				</Button>
				<Button
					onClick={handleConfirm}
					color="primary"
					variant="contained"
					disabled={!selectedDevedor || !selectedTitulo}
					startIcon={<SyncIcon />}
				>
					Sincronizar
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default SyncContactModal;
